'use client';

import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import useShortlistStore from '@/lib/store/shortlist';
import { createFragmentParser } from '@liquorice/gql-utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import CtaButton from '../CtaButton';
import Box from '../ui/Box';
import Btn from '../ui/Btn';
import Flex, { FlexProps } from '../ui/Flex';
import Txt from '../ui/Txt';

type ShortlistCardProps = FlexProps;

const ShortlistCard = (props: ShortlistCardProps) => {
  const [copy, setCopy] = useState<boolean>(false);

  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('actions');

  const getId = createFragmentParser(ENTRY_BASE_FRAGMENT, (data) => data?.id);

  const events = useShortlistStore((s) => s.events);
  const eventIds = events?.map((event) => `${getId(event)}`);

  const uriString = eventIds?.join(',');

  const pushNewParams = () => {
    router.push(`/shortlist/?id=${uriString}`);
  };

  const TIMEOUT = 1500;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (copy) {
      timeoutId = setTimeout(() => setCopy(false), TIMEOUT);
    }
    return () => clearTimeout(timeoutId);
  }, [copy]);

  const handleClick = () => {
    setCopy(true);
    navigator.clipboard.writeText(`${window.location.origin}${pathname}?${uriString}`);
  };

  return (
    <Flex rowGap="2xs" bordered cx={{ p: 'sm' }} {...props}>
      <Txt variant="tiny" uppercase>
        {t('shareShortlist')}
      </Txt>
      <Box>
        <CtaButton onClick={pushNewParams} color="black" underline="always">
          {t('linkToShortlist')}
        </CtaButton>
      </Box>
      <Box>
        <CtaButton onClick={handleClick} color="black" underline="always">
          {t('copyLink')}
        </CtaButton>
        <AnimatePresence>
          {copy && (
            <Btn
              as={motion.button}
              initial={{ opacity: 0, x: -5 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              color="black"
              variant="text"
              size="tiny"
              underline="none"
              endIcon="check"
              cx={{ mL: 'md' }}>
              {t('copied')}
            </Btn>
          )}
        </AnimatePresence>
      </Box>
    </Flex>
  );
};

export default ShortlistCard;
